import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { Box, Text, theme, useBreakpoint } from '@factorialco/deprecated-design-system'
import { F0Button, F0Icon } from '@factorialco/f0-react'
import { F0AvatarModule } from '@factorialco/f0-react'
import { Cross, Pig } from '@factorialco/f0-react/icons/app'
import { InAppCommsSidebarCard, Maybe } from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useEffect } from 'modules/core/hooks/useEffect'
import { track, trackingEvents } from 'modules/core/lib/Tracking'

import Media from 'modules/in_app_comms/components/Media'
import {
  buildMarketplaceLink,
  extractPathname,
  processLink,
} from 'modules/in_app_comms/lib/marketplaceLinks'
import { moduleAvailableToModuleId } from 'modules/upsell/lib/moduleIcons'
import { useLocalStorageWithExpiration } from 'modules/upselling_kit/utils/localStorage'

const CustomAvatar = () => {
  return (
    <Box
      background="grey1000"
      border={{ all: { style: 'solid', width: 's1', color: 'grey1000' } }}
      borderRadius={{ all: 'abs012' }}
      style={{
        width: '36px',
        height: '36px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <F0Icon icon={Pig} size="md" color="#FFFFFF" />
    </Box>
  )
}

interface SidebarCardProps {
  content: Maybe<InAppCommsSidebarCard> | undefined
  componentName: string
  onDismiss: () => void
}

const SidebarCard = ({ content, componentName, onDismiss }: SidebarCardProps) => {
  const [isHovered, setIsHovered] = useState(false)
  const { isMobile } = useBreakpoint()
  const { email, company } = useCurrentSession()
  const navigate = useNavigate()
  const showCloseButton = isMobile || isHovered

  // Read from localStorage for upsell_origin
  const [storedUpsellOrigin] = useLocalStorageWithExpiration<{
    request_source: string
    request_source_category?: string
    request_source_component?: string
  }>('upsell_origin', 5)
  const handleClose = (e?: React.MouseEvent) => {
    e?.stopPropagation()
    onDismiss()
    track(trackingEvents.inAppComms.dismissInAppCommsComponent, {
      component: 'in_app_comms_sidebar_card',
      componentName,
    })
  }

  const triggerEvent = (action: string) => {
    track(trackingEvents.inAppComms.clickInAppCommsComponent, {
      componentType: 'in_app_comms_sidebar_card',
      componentName,
      buttonAction: action,
    })
  }

  useEffect(() => {
    if (!content?.title) return
    track(trackingEvents.inAppComms.viewInAppCommsComponent, {
      component: 'in_app_comms_sidebar_card',
      componentName,
    })
  }, [content?.title, componentName])

  const colorsTheme = () => {
    switch (content?.backgroundColor) {
      case 'black_friday_gradient':
        return {
          background:
            'linear-gradient(180deg, transparent 0%, transparent 30%, rgba(10, 10, 15, 0.4) 55%, rgba(10, 10, 15, 0.85) 85%, rgba(5, 5, 10, 0.95) 100%), linear-gradient(90deg, #1a1a28 0%, #232336 15%, #2f3545 25%, #3f4a5a 35%, #4f5f70 45%, #607080 55%, #8a5068 70%, #a8425f 82%, #c43d5f 92%, #8b3545 100%)',
          titleColor: 'white' as const,
          descriptionColor: 'grey200' as const,
          iconComponent: !content.media?.[0]?.url ? <CustomAvatar /> : undefined,
        }
      default:
        return {
          background: 'grey000White',
          titleColor: 'grey1000' as const,
          descriptionColor: 'grey800' as const,
          iconComponent: content?.moduleIcon ? (
            <F0AvatarModule module={moduleAvailableToModuleId(content?.moduleIcon)} size="lg" />
          ) : undefined,
        }
    }
  }

  if (!content) {
    return null
  }

  return (
    <Box
      borderRadius={{ all: 'abs016' }}
      position="fixed"
      style={{
        bottom: isMobile ? theme.dimensions.s12 : theme.dimensions.s64,
        left: theme.dimensions.s12,
        zIndex: theme.zIndex.modals,
        maxWidth: '216px',
        width: '100%',
        boxShadow: theme.shadows.s50,
      }}
    >
      <Box
        background={isHovered ? 'grey100' : 'grey000White'}
        borderRadius={{ all: 'abs016' }}
        position="relative"
        style={{
          background: colorsTheme().background,
          overflow: 'hidden',
        }}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        onClick={() => {
          let processedLink = processLink(content.cardLink?.link ?? undefined, email, company?.id)
          triggerEvent(`Open link: ${content.cardLink?.link}`)
          if (content.cardLink?.isInternalLink) {
            processedLink = buildMarketplaceLink(
              processedLink,
              'in_app_comms_sidebar_card',
              componentName,
              storedUpsellOrigin
            )
            const pathname = extractPathname(processedLink)
            if (content.cardLink?.openInNewTab) {
              window.open(pathname, '_blank')
            } else {
              navigate(pathname)
            }
          } else {
            if (content.cardLink?.openInNewTab) {
              window.open(processedLink, '_blank')
            } else {
              window.location.href = processedLink
            }
          }
          onDismiss()
        }}
      >
        <Box
          position="absolute"
          style={{
            top: '8px',
            right: '8px',
            zIndex: theme.zIndex.modals + 1,
            opacity: showCloseButton ? '1' : '0',
            transition: 'opacity 0.2s ease-in-out',
          }}
        >
          <F0Button
            onClick={handleClose}
            variant="outline"
            size="sm"
            icon={Cross}
            hideLabel
            round
            label="Close"
            style={{
              backgroundColor: theme.colors.grey000White,
              borderRadius: theme.dimensions.s8,
              opacity: '0.7',
            }}
          />
        </Box>
        {!content.moduleIcon && <Media content={content} />}
        <Box padding="s12" gap="s12">
          {colorsTheme().iconComponent}
          <Box gap="s4">
            <Text.Normal
              size="200"
              weight="semibold"
              ellipsis
              lineClamp={2}
              color={colorsTheme().titleColor}
            >
              {content.title}
            </Text.Normal>
            <Text.Normal size="200" ellipsis lineClamp={3} color={colorsTheme().descriptionColor}>
              {content.description}
            </Text.Normal>
          </Box>
        </Box>
      </Box>
    </Box>
  )
}

export default SidebarCard
